import Image from "next/image";
import Link from "next/link";
import { ProductJsonLd } from "next-seo";
import { useRouter } from "next/router";
import { FiChevronRight, FiMinus, FiPlus } from "react-icons/fi";
// import { FaStar } from "react-icons/fa"; // Not used in the provided snippet
import { IoStarOutline, IoStarSharp, IoStarHalf } from "react-icons/io5";
import { GiDroplets } from "react-icons/gi";
import { HiInformationCircle } from "react-icons/hi";
import Head from "next/head";
import { usePostHog } from 'posthog-js/react';

//Modal
import { Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'

import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";

//internal import
import Layout from "@layout/Layout";
import Tags from "@component/common/Tags";
import Stock from "@component/common/Stock";
import Price from "@component/common/Price";
import Card from "@component/slug-card/Card";
import useAddToCart from "@hooks/useAddToCart";
import Discount from "@component/common/Discount";
import ProductServices from "@services/ProductServices";
import ProductCard from "@component/product/ProductCard";
import ReviewServices from "@services/ReviewServices";
import ReviewDrawer from "@component/review/ReviewDrawer";
import Countdown from "@component/notice/countdown"
// import ProcessingCountdown from "@component/notice/processingcountdown" // Not used in the provided snippet

import PaginateReview from "@component/review/PaginateReview";
import { useContext, useEffect, useState } from "react";
import { SidebarContext } from "@context/SidebarContext";
import { Input } from "@windmill/react-ui"; // Label was imported but not used

const ProductScreen = ({
  relatedProduct,
  product,
  overallRating,
  reviewCount,
  reviewForSeo,
  offerForSeo,
}) => {
  const router = useRouter();
  const posthog = usePostHog();
  const { handleAddItem, setItem, item } = useAddToCart();
  const { isUpdate, openCartDrawer } = useContext(SidebarContext);
  const [addOn, setAddOn] = useState({
    addOnName: "",
    addOnPrice: 0,
  });
  const [review, setReview] = useState([]);
  const cancelButtonRef = useRef(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // Ensure product._id exists before fetching reviews
    if (product?._id) {
      ReviewServices.getShowingReviewbyProductId(product._id)
        .then((res) => {
          if (res.data.length > 0) {
            setReview(res.data);
          }
        })
        .catch((err) => {
          // Avoid crashing if the review fetch fails after initial load
          console.error("Error fetching reviews:", err.message);
          setReview([]); // Set to empty on error
        });
    } else {
        setReview([]); // Set empty if no product ID
    }


    return () => {
      setReview([]);
    };
  }, [isUpdate, product]); // Depend on product object directly

  useEffect(() => {
    // Reset the addOn state whenever the product changes
    setAddOn({
      addOnName: "",
      addOnPrice: 0,
    });
    // Also reset item count when product changes
    setItem(1);
      // PostHog Product Viewed event
    if (product?._id && posthog) {
      posthog.capture('Product Viewed', {
        product_id: product._id,
        sku: product.sku,
        category: product.parent,
        name: product.title,
        brand: product.brand || null,
        variant: product.variant || null,
        price: product.price,
        quantity: 1,
        coupon: null, // Add coupon if available
        currency: "BDT",
        position: null, // Add position if available from search/category context
        value: product.price,
        url: `https://www.cubenationshop.com/product/${router.query.slug}` || null,
        image_url: product.image || null,
        originalPrice: product.originalPrice || 0,
        discount: product.discount || 0,
      });
    }
  }, [product, setItem, posthog]); // Depend on product, setItem, and posthog

  //comment this when using getServerSideProps
  if (router.isFallback) {
    return (
      <div className="w-full relative h-screen p-6 text-lg font-medium text-gray-600 dark:text-gray-400 dark:bg-gray-900">
        <div
          className=" absolute "
          style={{
            top: "50%",
            left: "50%",
            transform: `translate(-50%, -50%)`,
          }}
        >
          <div role="status">
            <svg
              aria-hidden="true"
              className="mx-1 w-14 h-14 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" // Fixed class attribute
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            {/* Removed duplicate spinner SVG */}
            <span className="sr-only">Loading...</span> {/* Added for accessibility */}
          </div>
        </div>
      </div>
    );
  }

  // If product is null/undefined after fallback (should not happen with blocking fallback + redirect, but good defense)
  if (!product) {
      // Optional: You could render a specific "Product not found" message here
      // or rely on the redirect which should have already happened server-side.
      // For safety, redirect client-side if somehow reached here without a product.
      if (typeof window !== 'undefined') {
          router.replace('/');
      }
      return ( // Return minimal loading/null state during client-side redirect attempt
        <div className="w-full relative h-screen p-6 text-lg font-medium text-gray-600 dark:text-gray-400 dark:bg-gray-900">
            Redirecting...
        </div>
      );
  }


  return <>
    {/* Render only if product exists */}
    <Layout title={product?.title} description={product?.description}>

      <Head>
        <meta property="og:title" content={`${product?.title} - CubeNation`} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={`${product?.image}` || `/logo/cb-logo.png`} />
        <meta property="og:description" content={`Price: ${product?.price} BDT - ${product?.description}` || `CubeNation is one of the largest Speed cube shops in BD. It's simply a Puzzle Bazaar & cube collection store!`} />
        <meta property="og:url" content={`https://www.cubenationshop.com/product/${router.query.slug}`} />
        <meta property="product:price:amount" content={`${product?.price}`} />
        <meta property="product:price:currency" content="BDT" />
      </Head>
      <div className="px-0 py-10 lg:py-10">
        <div className="mx-auto px-3 lg:px-10 max-w-screen-2xl">
          <div className="flex items-center pb-4">
            <ul className="flex items-center w-full overflow-hidden font-serif">
              <li className="text-sm pr-1 transition duration-200 ease-in cursor-pointer hover:text-emerald-500 font-semibold">
                <Link href="/">
                  Home
                </Link>
              </li>
              <li className="text-sm mt-[1px]">
                {" "}
                <FiChevronRight />{" "}
              </li>
              <li className="text-sm pl-1 transition duration-200 ease-in cursor-pointer hover:text-emerald-500 font-semibold ">
                <Link
                  href={`/search?category=${product?.parent
                    ?.toLowerCase()
                    .replace("&", "")
                    .split(" ")
                    .join("-")}`}
                >
                  {product?.parent}
                </Link>
              </li>
              <li className="text-sm mt-[1px]">
                {" "}
                <FiChevronRight />{" "}
              </li>
              <li className="text-sm px-1 transition duration-200 ease-in ">
                {product?.title}
              </li>
            </ul>
          </div>
          <div className="w-full rounded-lg p-3 lg:p-12 bg-white">
            <div className="flex flex-col xl:flex-row">
              <div className=" flex-shrink-0 xl:pr-10 lg:block w-full mx-auto md:w-6/12 lg:w-5/12 xl:w-4/12">
                <Discount product={product} slug={true} />
                <Image
                  src={product?.image || "/logo/cb-logo.png"}
                  alt={product?.title || 'Product Image'}
                  width={650}
                  height={650}
                  priority={true} // Add priority for LCP image
                  placeholder="blur"
                  blurDataURL="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                  style={{
                    maxWidth: "100%",
                    height: "auto"
                  }}
                />
              </div>
              <div className="w-full">
                <div className="flex flex-col md:flex-row lg:flex-row xl:flex-row">
                  <div className="w-full md:w-7/12 md:pr-4 lg:pr-4 xl:pr-12">
                    <div className="mb-6">
                      <h1 className="leading-7 text-lg md:text-xl lg:text-2xl mb-1 font-semibold font-serif text-gray-800">
                        {product?.title}
                      </h1>
                      <ProductJsonLd
                        productName={product?.title || 'Product'}
                        images={product?.image || "/logo/cb-logo.png"}
                        description={product?.description || 'Product Description'}
                        brand="CubeNation"
                        color="Stickerless" // Consider making dynamic if applicable
                        material="Plastic" // Consider making dynamic if applicable
                        releaseDate={product?.createdAt}
                        reviews={reviewForSeo || []} // Ensure it's an array
                        aggregateRating={{
                          ratingValue: overallRating || 0,
                          reviewCount: reviewCount || 0,
                        }}
                        // Ensure offerForSeo is valid JSON before parsing
                        offers={offerForSeo ? [JSON.parse(offerForSeo)] : []}
                      />
                      <p className="uppercase font-serif font-medium text-gray-500 text-sm hidden">
                        SKU :{" "}
                        <span className="font-bold text-gray-600">
                          {product?.sku
                            ? product.sku
                            : product?._id?.substring(18, 24) || 'N/A'}
                        </span>
                      </p>
                      <div className="flex font-serif font-medium text-gray-500 text-sm item">
                        <span
                          style={{ marginTop: "3px" }}
                          className="font-bold flex text-gray-600"
                          aria-label={`Rating: ${overallRating} out of 5 stars`}
                        >
                          {[...Array(5)].map((star, i) => {
                            let ratingValue = i + 1;
                            return (
                              <label key={i} className="cursor-default"> {/* Use label for semantics */}
                            
                                {ratingValue <= overallRating ? (
                                  <IoStarSharp aria-hidden="true" />
                                ) : ratingValue - overallRating > 0 &&
                                  ratingValue - overallRating < 1 ? (
                                  <IoStarHalf aria-hidden="true" />
                                ) : (
                                  <IoStarOutline aria-hidden="true" />
                                )}
                              </label>
                            );
                          })}{" "}
                        </span>
                          <span>
                          {" "}
                          ({reviewCount > 0 ? `${reviewCount} Customer Reviews` : "No Reviews Yet"}) {/* Corrected typo */}
                        </span>
                        <span
                          className={
                            product?.totalSalesCount > 0 // Added optional chaining
                              ? "bg-gray-700 text-white rounded-full inline-flex items-center justify-center px-2 mx-2 py-1 text-xs font-semibold font-serif"
                              : "hidden"
                          }
                        >
                          {product?.totalSalesCount} pcs sold
                        </span>

                      </div>
                    </div>
                    <Price product={product} />

                    {product?.price < product?.originalPrice ? (
                       <Countdown />) : null}

                    <div className="mb-4 md:mb-5 block">
                       <Stock product={product} />
                    </div>
                    <div>
                      <div className="text-sm leading-6 text-gray-500 md:leading-7">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product?.description || '', // Provide default empty string
                          }}
                        ></div>
                      </div><br />
                      {product?.parent === "3x3x3" && (
                        <div className="text-gray-700">

                          <div className="font-bold italic inline-flex items-center">
                            <span>Lubrication Service {" "}</span>
                            <span className="">
                              <button className="cursor-pointer ml-1" title="More Details" onClick={() => setOpen(true)}> {/* Added margin */}
                                 <HiInformationCircle aria-label="More details" />
                              </button>
                            </span>
                          </div>

                          {/* Lubing Option */}
                          <div className="grid w-full gap-5 md:grid-cols-1 align-middle mt-2"> {/* Added margin top */}
                            <Input
                                // Use state to control checked status if needed, otherwise rely on peer selector
                                onChange={(e) => { // Use onChange for better control
                                if (e.target.checked) {
                                    setOpen(true) // Open modal when checked
                                    setAddOn({
                                    addOnName: "Lubing Service",
                                    addOnPrice: 180,
                                    });
                                } else {
                                    setAddOn({
                                    addOnName: "",
                                    addOnPrice: 0,
                                    });
                                }
                                }}
                                type="checkbox" id="react-option" value="lubing" className="hidden peer" required="" />
                            <label htmlFor="react-option" className="flex items-center justify-between w-full p-5 text-gray-800 bg-red-50 border-2 border-gray-200 rounded-lg cursor-pointer peer-checked:border-green-600 peer-checked:bg-green-200 hover:text-gray-600 peer-checked:text-gray-600 hover:bg-red-200"> {/* Use htmlFor */}
                              <div className="block">
                                <div className="w-full text-sm flex item-center font-semibold">
                                  <span className="text-xl mr-3 text-blue-400"><GiDroplets /></span>
                                  <span className="w-full text-sm item-center"> Yes Please! +৳ 180</span></div>
                              </div>
                            </label>
                          </div>

                          {/* Lubing Modal */}
                          <Transition.Root show={open} as={Fragment}>
                            <Dialog as="div" className="relative z-30" initialFocus={cancelButtonRef} onClose={setOpen}>
                              <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                              </Transition.Child>

                              <div className="fixed inset-0 z-10 overflow-y-auto"> {/* Added overflow-y-auto */}
                                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                  <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                  >
                                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all mb-6 sm:my-8 sm:w-full sm:max-w-lg">
                                      <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <HiInformationCircle className="h-6 w-6 text-red-600" aria-hidden="true" /> {/* Use aria-hidden */}
                                          </div>
                                          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                                              Lubrication Service
                                            </Dialog.Title>
                                            <div className="mt-2">
                                              <p className="text-gray-500 text-sm">
                                                We will professionally lubricate this puzzle to make it perform its best. It takes an additional 24 hours to process the order when you select this option.
                                              </p>
                                              <p className="text-red-700 text-sm mt-2"> {/* Added margin top */}
                                                This is a service. We will not provide any lube bottles in this. If you are looking for lube bottles, please search <a href="https://www.cubenationshop.com/search?query=lube&amp;utm_source=lubrication" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">"lube"</a> instead of selecting this option.</p> <br></br>
                                              <div className="my-4"> {/* Added margin for spacing */}
                                                <Image
                                                  width={420}
                                                  height={320} // Adjust height based on aspect ratio if needed
                                                  src="/lubing service.jpg"
                                                  alt="Applying lubes(Dignitas) to the cube according to the service"
                                                  placeholder="blur"
                                                  blurDataURL="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                                                  sizes="(max-width: 768px) 100vw, 33vw"
                                                  style={{ maxWidth: '100%', height: 'auto' }} // Responsive image style
                                                />
                                              </div>                                              <Link
                                                href="/lubrication"
                                                className="text-blue-800 text-xs italic cursor-pointer align-middle underline" 
>
                                                More Details
                                              </Link>
                                            </div>

                                          </div>
                                        </div>
                                      </div>
                                      <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button
                                          type="button"
                                          className="inline-flex w-full justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 sm:ml-3 sm:w-auto" // Style agreement button
                                          onClick={() => setOpen(false)}
                                        >
                                          I Understand
                                        </button>

                                      </div>
                                    </Dialog.Panel>
                                  </Transition.Child>
                                </div>
                              </div>
                            </Dialog>
                          </Transition.Root>
                        </div>)} {/* End 3x3x3 parent check */}

                      <div className="flex items-center mt-4">
                        <div className="flex items-center justify-between space-s-3 sm:space-s-4 w-full">
                          <div className="group flex items-center justify-between rounded-md overflow-hidden flex-shrink-0 border h-11 md:h-12 border-gray-300">
                            <button
                              onClick={() => setItem(item - 1)}
                              disabled={item <= 1} // Simplified condition
                              className={` ${item <= 1
                                  ? "text-gray-400 cursor-not-allowed" // Added cursor-not-allowed
                                  : "hover:text-gray-500"
                                } flex items-center justify-center flex-shrink-0 h-full transition ease-in-out duration-300 focus:outline-none w-8 md:w-12 text-heading border-e border-gray-300`}
                                aria-label="Decrease quantity"
                            >
                              <span className="text-dark text-base">
                                <FiMinus />
                              </span>
                            </button>
                            <p className="font-semibold flex items-center justify-center h-full transition-colors duration-250 ease-in-out cursor-default flex-shrink-0 text-base text-heading w-8 md:w-20 xl:w-24">
                              {item}
                            </p>
                            <button
                              onClick={() => setItem(item + 1)}
                              disabled={
                                (product?.quantity !== undefined && product.quantity < item && !product?.ispreOrderAllowed) ||
                                (product?.quantity !== undefined && product.quantity === item && !product?.ispreOrderAllowed)
                              }
                              className="flex items-center justify-center h-full flex-shrink-0 transition ease-in-out duration-300 focus:outline-none w-8 md:w-12 text-heading border-s border-gray-300 hover:text-gray-500 disabled:text-gray-400 disabled:cursor-not-allowed" // Added disabled styles
                              aria-label="Increase quantity"
                            >
                              <span className="text-dark text-base">
                                <FiPlus />
                              </span>
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              openCartDrawer();
                              handleAddItem(product, addOn);
                            }}
                            disabled={
                                product?.quantity < 1 && !product?.ispreOrderAllowed
                            }
                            className={
                                `text-sm leading-4 inline-flex items-center font-semibold font-serif text-center justify-center border-0 border-transparent rounded-md focus-visible:outline-none focus:outline-none text-white px-4 ml-4 md:px-6 lg:px-8 py-4 md:py-3.5 lg:py-4 w-full h-12 transition ease-in-out duration-300 ${
                                  (product?.quantity < 1 && !product?.ispreOrderAllowed)
                                    ? "bg-gray-300 cursor-not-allowed" // Disabled style
                                    : "bg-emerald-950 hover:bg-emerald-1000 cursor-pointer" // Enabled style
                                }`
                            }
                          >
                            { (product?.quantity < 1 && product?.ispreOrderAllowed) ? "Pre-Order Now" : "Add To Cart" }
                          </button>
                        </div>
                      </div>
                      <div className="border-t-[1px] text-gray-700 font-semibold border-gray-300 mt-6 pt-3"> {/* Adjusted border color and spacing */}
                        <div className=" px-4 flex justify-between items-center border-b-[1px] border-gray-300 py-2">
                          <h5>Unit Price ৳ {product?.price}</h5>
                          <h5>৳ {(product?.price * item).toFixed(2)}</h5> {/* Added toFixed for currency */}
                        </div>
                        {addOn.addOnPrice > 0 && (
                          <div className=" px-4 flex justify-between items-center border-b-[1px] border-gray-300 py-2">
                            <h5>Lubing Service</h5>
                            <h5>৳ {(addOn.addOnPrice * item).toFixed(2)}</h5>
                          </div>
                        )}
                        <div className=" px-4 flex justify-between items-center border-b-[1px] border-gray-300 py-2 text-lg"> {/* Made total larger */}
                          <h5>Total </h5>
                          <h5>
                            ৳{" "}
                            {(product?.price * item + addOn.addOnPrice * item).toFixed(2)}
                          </h5>
                        </div>
                      </div>
                      <div className="flex flex-col mt-4">
                        <span className="font-serif font-semibold py-1 text-sm d-block">
                          <span className="text-gray-700">Category:</span>{" "}
                          <span className="text-gray-500 hover:text-emerald-600"> {/* Added hover effect */}
                            <Link
                                href={`/search?category=${product?.parent
                                    ?.toLowerCase()
                                    .replace("&", "")
                                    .split(" ")
                                    .join("-")}`}
                            >
                                {product?.parent}
                            </Link>
                          </span>
                        </span>
                        <Tags product={product} />
                      </div>
                      {/* social share */}
                      <div className="mt-8">
                        <h3 className="text-base font-semibold mb-1 font-serif">
                          Like this product?
                        </h3>
                        <p className="font-sans text-sm text-gray-500">
                          Share it with your peers!
                        </p>
                        <ul className="flex mt-4">
                          <li className="flex items-center text-center border border-gray-100 rounded-full hover:bg-emerald-950 mr-2 transition ease-in-out duration-500">
                            <FacebookShareButton
                              url={`https://www.cubenationshop.com/product/${router.query.slug}`}
                              quote={`Check out ${product?.title} on CubeNation!`} // Improved quote
                            >
                              <FacebookIcon size={32} round />
                            </FacebookShareButton>
                          </li>
                          <li className="flex items-center text-center border border-gray-100 rounded-full hover:bg-emerald-950 mr-2 transition ease-in-out duration-500">
                            <TwitterShareButton
                              url={`https://www.cubenationshop.com/product/${router.query.slug}`}
                              title={`Check out ${product?.title} on CubeNation!`} // Use title for Twitter
                            >
                              <TwitterIcon size={32} round />
                            </TwitterShareButton>
                          </li>
                          <li className="flex items-center text-center border border-gray-100 rounded-full hover:bg-emerald-950 mr-2 transition ease-in-out duration-500">
                            <RedditShareButton
                              url={`https://www.cubenationshop.com/product/${router.query.slug}`}
                              title={`Check out ${product?.title} on CubeNation!`} // Use title for Reddit
                            >
                              <RedditIcon size={32} round />
                            </RedditShareButton>
                          </li>
                          <li className="flex items-center text-center border border-gray-100 rounded-full hover:bg-emerald-950 mr-2 transition ease-in-out duration-500">
                            <WhatsappShareButton
                              url={`https://www.cubenationshop.com/product/${router.query.slug}`}
                              title={`Check out ${product?.title} on CubeNation!`} // Use title for WhatsApp
                              separator=" :: "
                            >
                              <WhatsappIcon size={32} round />
                            </WhatsappShareButton>
                          </li>
                          <li className="flex items-center text-center border border-gray-100 rounded-full hover:bg-emerald-950 mr-2 transition ease-in-out duration-500">
                            <LinkedinShareButton
                              url={`https://www.cubenationshop.com/product/${router.query.slug}`}
                              // title, summary, source are optional for LinkedIn
                            >
                              <LinkedinIcon size={32} round />
                            </LinkedinShareButton>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  {/* Card Component */}
                  <div className="w-full xl:w-5/12 lg:w-6/12 md:w-5/12">
                    <div className="mt-6 md:mt-0 lg:mt-0 bg-gray-50 border border-gray-100 p-4 lg:p-8 rounded-lg">
                      <Card />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* review section */}
          <div className="pt-10 lg:pt-20 lg:pb-10">
            <h3 className="leading-7 text-lg lg:text-xl mb-3 font-semibold font-serif hover:text-gray-600">
              Reviews
            </h3>
            <ReviewDrawer product={product?._id} />
            <br />
            {/* Pass the review state fetched in useEffect */}
            <PaginateReview review={review} />
          </div>

          {/* related products */}
          {relatedProduct && relatedProduct.length > 0 && ( // Only show if related products exist
            <div className="pt-10 lg:pt-20 lg:pb-10">
              <h3 className="leading-7 text-lg lg:text-xl mb-3 font-semibold font-serif hover:text-gray-600">
                Similar Products for you
              </h3>
              <div className="flex">
                <div className="w-full">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-6 gap-2 md:gap-3 lg:gap-3">
                    {relatedProduct.map((productItem) => (
                      <ProductCard key={productItem._id} product={productItem} /> // Use unique _id as key
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  </>;
};


// --- DATA FETCHING ---

export const getStaticProps = async (context) => {
  const { slug } = context.params;

  try {
    // Fetch the product
    const products = await ProductServices.getProductBySlug(slug);

    // --- CHECK IF PRODUCT EXISTS ---
    if (!products) {
      // Product not found for this slug, redirect to homepage
      console.log(`Product with slug "${slug}" not found.`);
      return { notFound: true };
    }
    // --- END CHECK ---

    // Product exists, proceed with the rest of the logic
    // Exclude costOfGoods if it exists and you don't need it client-side
    const { costOfGoods, ...product } = products;

    let d = new Date();
    d.setMonth(d.getMonth() + 12); // Set expiry for Schema

    // Determine availability for Schema
    let availability = "";
    if (product.quantity <= 0 && product.ispreOrderAllowed) {
        availability = "PreOrder";
    } else if (product.quantity <= 0) {
        availability = "Discontinued";
    } else {
        availability = "InStock";
    }

    // Initialize review/offer data
    let overallRating = 0;
    let reviewCount = 0;
    let reviewForSeo = []; // Default to empty array
    let offerForSeo = JSON.stringify({
      "@type": "Offer", // Add type for Schema
      price: product?.originalPrice || product?.price, // Use originalPrice or price
      priceCurrency: "BDT",
      priceValidUntil: d.toISOString().split('T')[0], // Format as YYYY-MM-DD
      itemCondition: "https://schema.org/NewCondition",
      availability: `https://schema.org/${availability}`,
      url: `https://www.cubenationshop.com/product/${slug}`, // Use slug here
      seller: {
        "@type": "Organization", // Add type for Schema
        name: "CubeNation",
      },
    });

    // Fetch reviews only if product._id exists (it should at this point)
    const reviewsResponse = await ReviewServices.getShowingReviewbyProductId(
      product._id
    );

    // Process reviews if available
    if (reviewsResponse && reviewsResponse.data && reviewsResponse.data.length > 0) {
      reviewForSeo = reviewsResponse.reviewForSeo || []; // Assign reviews for SEO
      // Check if reviewAvg exists and has properties
      if (reviewsResponse.reviewAvg && reviewsResponse.reviewAvg.overallRating !== undefined) {
        overallRating = reviewsResponse.reviewAvg.overallRating;
      }
       if (reviewsResponse.reviewAvg && reviewsResponse.reviewAvg.reviewCount !== undefined) {
        reviewCount = reviewsResponse.reviewAvg.reviewCount;
      }
    }

    // Fetch related products based on tags
    let relatedProduct = [];
    if (product.tag && product.tag.length > 0) { // Check if tags exist
        relatedProduct = await ProductServices.search20ProductBytags(product.tag);
    }


    return {
      props: {
      product,
      relatedProduct,
        overallRating,
        reviewCount,
        reviewForSeo,
        offerForSeo,
      },
      revalidate: 86400, // Revalidate every 24 hours (1 day)
    };

  } catch (error) {
    // Log the error for server-side debugging
    console.error(`Error in getStaticProps for slug "${slug}":`, error);

    // Redirect to homepage on any error during data fetching for this slug
    return { notFound: true };
  }
};


export const getStaticPaths = async () => {
  // Skip path generation during development or specific builds if needed
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: "blocking", // Use 'blocking' so server renders page if path not generated
    }
  }

  try {
    // Fetch slugs for all products you want to pre-render
    const products = await ProductServices.getShowingProducts();

    // Map products to the required path format
    const paths = products?.map((product) => ({
      params: { slug: product.slug },
    })) || []; // Ensure paths is an array even if products fetch fails or returns null

    return {
        paths,
        fallback: "blocking" // 'blocking' waits for SSR, 'true' shows fallback UI first, 'false' returns 404 for unknown paths
    };
  } catch (error) {
      console.error("Error fetching static paths:", error);
      // Return empty paths and let fallback handle it if fetching slugs fails
      return {
          paths: [],
          fallback: "blocking",
      };
  }
};

export default ProductScreen;